import { createReducer, on } from '@ngrx/store';
import * as LocationCardActions from '../actions';
import { findFieldById } from '../configs/base-utils';
import { createSmartTranslationMessage } from '../models/utils';
import {
    NormalizedFieldState,
    NormalizedFieldExtras,
    NormalizedRowConfig,
    NormalizedSection,
    normalizeFormState,
    normalizeSection,
    NormalizedSectionField,
    isFormField,
} from '../config.models';
import { getGlobalConfigBySeries, initialFormConfig } from '../configs/initial/initial-config';
import { MonitorSeriesNames, TritonSensorNames } from '../../location-card.constants';
import {
    extractTritonSensorsFromConfig,
    getTritonSensorValue,
    setTritonSensorPreferences,
    sortTritonSections,
    tritonCheckMp2,
} from '../configs/triton/utils';
import { getTritonSensorConfigByName } from '../configs/triton/base';
import { samplerConfig } from '../configs/triton/sensors/sampler';
import { findArraySectionById, findFieldByPath, generateSectionPath } from './helpers';
import { FieldItem, FieldType } from '../models/field-models';
import {
    ConfigureAlarmsFieldId,
    PipeInstallationFieldIds,
    PipeInstallationMp2FieldIds,
} from '../configs/common.models';
import { BasicInfoCommonFieldIds, CommonSectionIds, SensorTypes } from '../configs/common.models';
import { Mp2BasicInfoFieldId, Mp2ConfigureAlarmsFieldId, TritonSectionIds, Mp1SensorTitles, Mp2SensorTitles, getSensorKeyById } from '../configs/triton/base-config-models';
import { LongRangeFieldIds } from '../configs/echo/base-config-models';
import { EdgeRlRadarFieldIds } from '../configs/edge-rl/base-config-models';
import { initialState, LocationCardState } from './state';
import { SamplerDevice, TritonLocationCardState, TritonMonitorConfiguration } from '../configs/triton/models';
import { RowItemType } from '../models';
import {
    assignFieldValue,
    isBooleanValue,
    isDateValue,
    isNumberValue,
    isSelectValue,
    isStringValue,
} from '../models/utils';
import { LocationStatus } from 'app/shared/models/locations';
import { InstallationSeries } from 'app/shared/models/location-details';

const PIPE_ROUND_SHAPE = 'Pipe: 200';
const DEFAULT_MAINTANCE_INTERVAL = 2;

export const formReducer = createReducer(
    initialState,
    on(LocationCardActions.resetForm, () => {
        return initialState;
    }),

    on(LocationCardActions.setFormData, (state, { data }) => {
        return { ...state, locationCardData: data };
    }),

    on(LocationCardActions.initForm, (state) => {
        const formState = normalizeFormState(initialFormConfig(state as LocationCardState<never>));
        return { ...state, formState };
    }),

    // TODO: Seems bad, makes extra actions on TRITON
    on(LocationCardActions.populateForm, (state, { details, config, series }) => {
        let newState = { ...state, locationDetails: details, monitorConfiguration: config };

        const formState = normalizeFormState(getGlobalConfigBySeries(details[0].series, newState));
        newState = {
            ...newState,
            formState,
            options: { ...newState.options, monitorSeries: { data: series, loading: false } },
        };

        if (details[0].series === MonitorSeriesNames.triton) {
            const [sensor1, sensor2] = extractTritonSensorsFromConfig(config as TritonMonitorConfiguration);

            if (sensor1) {
                const sensor1Text = getTritonSensorValue(sensor1).text;

                const sensor1Config = getTritonSensorConfigByName(
                    sensor1Text as TritonSensorNames,
                    0,
                    0,
                    state.settings,
                    sensor1,
                );

                const normalizedSensor1 = normalizeSection(sensor1Config);
                normalizedSensor1.metadata = { sensorIndex: 0, mp: sensor1.installationNumber, type: SensorTypes.triton };
                const sensor1Key = getSensorKeyById(normalizedSensor1.id);
                normalizedSensor1.title = sensor1.installationNumber === 0 ? Mp1SensorTitles[sensor1Key] : Mp2SensorTitles[sensor1Key];

                formState.config.sections.push(normalizedSensor1);
            }

            if (sensor2) {
                const sensor2Text = getTritonSensorValue(sensor2).text;

                const sensor2Config = getTritonSensorConfigByName(
                    sensor2Text as TritonSensorNames,
                    1,
                    1,
                    state.settings,
                    sensor2,
                );
                const normalizedSensor2 = normalizeSection(sensor2Config);
                normalizedSensor2.metadata = {
                    sensorIndex: 1,
                    mp: sensor2.installationNumber,
                    type: SensorTypes.triton,
                };
                const sensor2Key = getSensorKeyById(normalizedSensor2.id);
                normalizedSensor2.title = sensor2.installationNumber === 0 ? Mp1SensorTitles[sensor2Key] : Mp2SensorTitles[sensor2Key];

                formState.config.sections.push(normalizedSensor2);
            }

            const samplerInfo = (config as TritonMonitorConfiguration).devices.find(
                (v) => v.name === 'Sampler',
            ) as SamplerDevice;
            if (samplerInfo) {
                const sampler = normalizeSection(samplerConfig(state.settings, samplerInfo));
                formState.config.sections.push(sampler);
            }

            const newTritonState = newState as TritonLocationCardState;
            tritonCheckMp2(newTritonState);
            sortTritonSections(formState);
            setTritonSensorPreferences(newTritonState);

            return {
                ...newState,
                options: { ...newState.options },
                autocompleteOptions: { ...newState.autocompleteOptions },
                formState,
            };
        }

        return {
            ...newState,
            options: { ...newState.options },
            autocompleteOptions: { ...newState.autocompleteOptions },
            formState,
        };
    }),

    on(LocationCardActions.updateFieldValueByPath, (state, { path, value }) => {
        const field = findFieldByPath(state.formState, path);

        if (field) {
            field.value = value;

            if (state.pristine) {
                return { ...state, pristine: false };
            }
        }

        return state;
    }),

    on(LocationCardActions.enableField, (state, { fieldId }) => {
        const newFormState = { ...state.formState };
        const field = findFieldById(newFormState, fieldId);

        if (field) {
            field.disabled = false;
        }

        return {
            ...state,
            formState: newFormState,
        };
    }),

    on(LocationCardActions.disableField, (state, { fieldId }) => {
        const newFormState = { ...state.formState };
        const field = findFieldById(newFormState, fieldId);

        if (field) {
            field.disabled = true;
        }

        return {
            ...state,
            formState: newFormState,
        };
    }),

    on(LocationCardActions.setFieldValidators, (state, { fieldId, validators }) => {
        const newFormState = { ...state.formState };
        const field = findFieldById(newFormState, fieldId);

        if (field && isFormField(field)) {
            field.validators = validators;
        }

        return { ...state, formState: newFormState };
    }),

    on(LocationCardActions.clearFieldValidators, (state, { fieldId }) => {
        const newFormState = { ...state.formState };
        const field = findFieldById(newFormState, fieldId);

        if (field && isFormField(field)) {
            field.validators = [];
        }

        return { ...state, formState: newFormState };
    }),

    // we don't know what type of data LocationCardState can hold
    on(LocationCardActions.setGlobalConfig, (state, { monitorSeriesName }) => {
        const oldInstallationTypeField = findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationType);
        const previousInstallationType = oldInstallationTypeField ? String(oldInstallationTypeField.value || '') : '';

        const newFormState = normalizeFormState(getGlobalConfigBySeries(monitorSeriesName, state));

        const mergedFormState = newFormState;

        // Define recursive field merger function
        function mergeFields(currentSection: NormalizedSection, newSection: NormalizedSection): void {
            if (!currentSection || !newSection) return;

            Object.keys(newSection.fields).forEach((fieldId: string) => {
                const newField: NormalizedFieldState = newSection.fields[fieldId];
                const currentField: NormalizedFieldState | undefined = currentSection.fields?.[fieldId];

                if (currentField && newField.preserveOldValues !== false) {
                    // Handle nested sections recursively

                    if (
                        currentField.type === FieldType.Section &&
                        newField.type === FieldType.Section &&
                        newField.section &&
                        currentField.section
                    ) {
                        mergeFields(currentField.section, newField.section);
                    } else {
                        // For all other field types, use the utility function to preserve values
                        assignFieldValue(newField, currentField.value);
                    }
                }
            });
        }

        if (state.formState?.config?.sections && mergedFormState.config?.sections) {
            mergedFormState.config.sections.forEach((newSection) => {
                const currentSection = state.formState.config.sections.find(
                    (s) => s.id === newSection.id && newSection.preserveOldValues !== false,
                );
                if (currentSection) {
                    mergeFields(currentSection, newSection);
                }
            });
        }

        const newState = { ...state, formState: mergedFormState };
        if (monitorSeriesName === MonitorSeriesNames.triton) {
            const newTritonState = newState as TritonLocationCardState;
            tritonCheckMp2(newTritonState);
            sortTritonSections(mergedFormState);
            setTritonSensorPreferences(newTritonState);
        }

        if (monitorSeriesName === MonitorSeriesNames.echo) {
            const manholeDepthField = findFieldById(mergedFormState, BasicInfoCommonFieldIds.ManholeDepth);
            const longRangeManholeField = findFieldById(mergedFormState, LongRangeFieldIds.ManholeDepth);
            if (manholeDepthField && longRangeManholeField) {
                assignFieldValue(longRangeManholeField, manholeDepthField.value);
            }
        }

        if (monitorSeriesName === MonitorSeriesNames.edgeRl) {
            const manholeDepthField = findFieldById(mergedFormState, BasicInfoCommonFieldIds.ManholeDepth);
            const radarManholeField = findFieldById(mergedFormState, EdgeRlRadarFieldIds.ManholeDepth);
            if (manholeDepthField && radarManholeField) {
                assignFieldValue(radarManholeField, manholeDepthField.value);
            }
        }

        return {
            ...newState,
            options: { ...state.options },
            metadata: { ...state.metadata, previousInstallationType, monitorSeriesName },
        };
    }),

    on(LocationCardActions.removeSection, (state, { sectionId, parentSectionId }) => {
        const formState = { ...state.formState };
        const parentSection = formState.config.sections.find((section) => section.id === parentSectionId);

        if (!parentSection) {
            console.error(`Target section ${parentSectionId} not found`);
            return state;
        }

        parentSection.rows = parentSection.rows.filter((row) => row.id !== sectionId);
        delete parentSection.fields[sectionId];

        return { ...state, formState };
    }),

    on(LocationCardActions.attachSectionToRow, (state, { rowId, sectionConfig, targetSectionId, fieldId }) => {
        const formState = { ...state.formState };

        const targetSection = formState.config.sections.find((section) => section.id === targetSectionId);
        if (!targetSection) {
            console.error(`Target section ${targetSectionId} not found`);
            return state;
        }

        const toAttachRowIndex = targetSection.rows.findIndex((row) => row.id === rowId);

        // Create path for the new section field
        const sectionPath = `${targetSectionId}.${fieldId}`;

        // Normalize the section config
        const normalizedSection = normalizeSection(
            sectionConfig,
            { sectionId: targetSectionId, rowId: fieldId },
            sectionPath,
        );

        // Create section field
        const sectionField: NormalizedFieldState = {
            id: fieldId,
            name: fieldId,
            type: RowItemType.Section,
            value: null,
            path: sectionPath,
            section: normalizedSection,
        };

        targetSection.fields[fieldId] = sectionField;

        const newRow: NormalizedRowConfig = {
            id: fieldId,
            panelClass: sectionConfig.info.panelClass || '',
            fields: [fieldId],
        };

        if (toAttachRowIndex !== -1) {
            targetSection.rows.splice(toAttachRowIndex + 1, 0, newRow);
        } else {
            targetSection.rows.push(newRow);
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.removeArraySectionItem, (state, { fieldId, index }) => {
        const formState = { ...state.formState };
        const arraySection = findArraySectionById(formState, fieldId);

        if (!arraySection) {
            console.error(`Array section with ID ${fieldId} not found`);
            return state;
        }

        const toRemoveRow = arraySection.rows[index];

        if (!toRemoveRow) {
            console.error(`Row at index ${index} not found in section ${fieldId}`);
            return state;
        }

        arraySection.rows = arraySection.rows.filter((row) => row.id !== toRemoveRow.id);
        toRemoveRow.fields.forEach((fieldId) => {
            delete arraySection.fields[fieldId];
        });

        for (let i = index; i < arraySection.rows.length; i++) {
            const row = arraySection.rows[i];

            // Update row ID: replace last number in ID pattern
            const oldRowId = row.id;
            const newRowId = oldRowId.replace(/-(\d+)$/, `-${i}`);
            row.id = newRowId;

            // 3. Update all fields in this row
            const updatedFields = [];

            row.fields.forEach((oldFieldId) => {
                // Skip if field doesn't exist
                if (!arraySection.fields[oldFieldId]) return;

                // Extract the field base name and create new ID
                const newFieldId = oldFieldId.replace(/-(\d+)$/, `-${i}`);

                // Only update if the ID actually changed
                if (newFieldId !== oldFieldId) {
                    // Clone field with updated ID and path
                    const field = { ...arraySection.fields[oldFieldId] };
                    field.id = newFieldId;
                    // Update field path: replace array index in brackets if it exists
                    if (field.path) {
                        field.path = field.path.replace(/-(\d+)$/, `-${i}`);
                    }
                    // Add as new field and remove old one
                    arraySection.fields[newFieldId] = field;
                    delete arraySection.fields[oldFieldId];

                    updatedFields.push(newFieldId);
                } else {
                    updatedFields.push(oldFieldId);
                }
            });

            // Update the row's fields array
            row.fields = updatedFields;
        }

        return { ...state, formState, pristine: false };
    }),

    on(LocationCardActions.addArraySectionItem, (state, { fieldId }) => {
        const formState = { ...state.formState };
        const arraySection = findArraySectionById(formState, fieldId);

        if (!arraySection) {
            console.error(`Array section with ID ${fieldId} not found`);
            return state;
        }

        if (!arraySection.itemGenerator) {
            console.error(`No itemGenerator found for section ${fieldId}`);
            return state;
        }

        const currentCount = arraySection.rows ? arraySection.rows.length : 0;
        if (arraySection.maxItems && currentCount >= arraySection.maxItems) {
            return state;
        }

        const newIndex = currentCount;
        const newRow = arraySection.itemGenerator(newIndex);
        const path = generateSectionPath(formState, arraySection);

        newRow.items.forEach((item: FieldItem) => {
            arraySection.fields[item.id] = {
                ...item,
                path: `${path}.${item.id}`,
                errors: {},
            };
        });

        if (!arraySection.rows) {
            arraySection.rows = [];
        }

        arraySection.rows.push({
            id: newRow.info.id,
            fields: newRow.items.map((item) => item.id),
            panelClass: newRow.info.panelClass,
        });

        return { ...state, formState };
    }),

    on(LocationCardActions.setArraySectionItems, (state, { fieldId, newItemsCount }) => {
        const formState = { ...state.formState };
        const arraySection = findArraySectionById(formState, fieldId);

        if (!arraySection) {
            console.error(`Array section with ID ${fieldId} not found`);
            return state;
        }

        if (!arraySection.itemGenerator) {
            console.error(`No itemGenerator found for section ${fieldId}`);
            return state;
        }

        const currentItemsCount = arraySection.rows.length;
        const path = generateSectionPath(formState, arraySection);

        if (newItemsCount > currentItemsCount) {
            for (let i = currentItemsCount; i < newItemsCount; i++) {
                const newRow = arraySection.itemGenerator(i);
                newRow.items.forEach((item: FieldItem) => {
                    arraySection.fields[item.id] = {
                        ...item,
                        path: `${path}.${item.id}`,
                        errors: {},
                    };
                });

                arraySection.rows.push({
                    id: newRow.info.id,
                    fields: newRow.items.map((item) => item.id),
                    panelClass: newRow.info.panelClass,
                });
            }
        } else if (currentItemsCount > newItemsCount) {
            arraySection.rows = arraySection.rows.slice(0, newItemsCount);
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.setFieldValueAction, (state, { fieldId, value }) => {
        const formState = { ...state.formState };
        const field = findFieldById(formState, fieldId);

        if (field) {
            assignFieldValue(field, value);
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.pipeHeightWidthChange, (state, { isHeight, isMp1 }) => {
        const formState = { ...state.formState };

        const selectedType = isMp1
            ? findFieldById(formState, BasicInfoCommonFieldIds.InstallationType).value
            : findFieldById(formState, Mp2BasicInfoFieldId.InstallationType).value;

        if (selectedType === PIPE_ROUND_SHAPE) {
            const heightField = isMp1
                ? findFieldById(formState, PipeInstallationFieldIds.Height, FieldType.Number)
                : findFieldById(formState, PipeInstallationMp2FieldIds.Height, FieldType.Number);

            const widthField = isMp1
                ? findFieldById(formState, PipeInstallationFieldIds.Width, FieldType.Number)
                : findFieldById(formState, PipeInstallationMp2FieldIds.Width, FieldType.Number);

            if (isHeight) {
                widthField.value = heightField.value;
            } else {
                heightField.value = widthField.value;

                const fullPipeThresholdFieldId = isMp1
                    ? ConfigureAlarmsFieldId.FullPipeThreshold
                    : Mp2ConfigureAlarmsFieldId.FullPipeThreshold;
                const fullPipeThresholdField = findFieldById(formState, fullPipeThresholdFieldId);
                if (fullPipeThresholdField) {
                    fullPipeThresholdField.value = heightField.value;
                }
            }

            return { ...state, formState };
        }

        return state;
    }),

    on(LocationCardActions.clearAutocomplete, (state, { fieldId }) => {
        const formState = { ...state.formState };
        const field = findFieldById(formState, fieldId);

        if (field) {
            field.value = null;
        }

        return { ...state, formState, pristine: false };
    }),

    on(LocationCardActions.elevationAlarmConversion, (state, { newInstallationSeries, installationTypeFieldId, alarmFieldIds, isMetric, elevationUnit, depthUnit }) => {
        const formState = { ...state.formState };
        const installationTypeField = findFieldById(formState, installationTypeFieldId);
        if (!installationTypeField || !installationTypeField.value) {
            return state;
        }

        const previousValue = String(installationTypeField.value);
        const previousShapeId = Number(previousValue.split(':')[1]?.trim());
        const wasElevation = previousShapeId >= InstallationSeries.Elevation;
        const isElevation = newInstallationSeries === InstallationSeries.Elevation;

        if (wasElevation === isElevation) {
            return state;
        }

        const conversionFactor = isMetric ? 1000 : 12;

        const lowLevelField = findFieldById(formState, alarmFieldIds.lowLevelThreshold, FieldType.Number);
        const highLevelField = findFieldById(formState, alarmFieldIds.highLevelThreshold, FieldType.Number);
        const highHighField = findFieldById(formState, alarmFieldIds.highHighThreshold, FieldType.Number);

        const thresholdFields = [lowLevelField, highLevelField, highHighField];

        thresholdFields.forEach((field) => {
            if (field && field.value != null) {
                const currentValue = Number(field.value);
                field.value = isElevation
                    ? Number((currentValue / conversionFactor).toFixed(2))
                    : Number((currentValue * conversionFactor).toFixed(2));
            }
        });

        const newSuffix = isElevation ? elevationUnit : depthUnit;
        thresholdFields.forEach((field) => {
            if (field) {
                field.placeholderSuffix = newSuffix;
            }
        });

        return { ...state, formState };
    }),

    on(LocationCardActions.statusChange, (state, { isMp1 }) => {
        const formState = { ...state.formState };
        const mp2 = formState.config.sections.find((section) => section.id === TritonSectionIds.Mp2BasicInfo);

        if (isMp1) {
            const newStatus = findFieldById(formState, BasicInfoCommonFieldIds.Status).value;
            const maintenanceIntervalField = findFieldById(formState, BasicInfoCommonFieldIds.MaintenanceInterval);
            const maintenanceEndField = findFieldById(formState, BasicInfoCommonFieldIds.MaintenanceEnd, FieldType.UI);

            if (maintenanceEndField) {
                if (newStatus === 1) {
                    maintenanceIntervalField.visible = true;
                    maintenanceEndField.visible = true;

                    maintenanceIntervalField.value = DEFAULT_MAINTANCE_INTERVAL;
                    maintenanceEndField.label = createSmartTranslationMessage(
                        'LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE',
                        {
                            date: 'unknown',
                        },
                    );
                } else {
                    maintenanceIntervalField.visible = false;
                    maintenanceEndField.visible = false;
                }
            }

            if (newStatus === LocationStatus.Inactive && mp2) {
                findFieldById(formState, Mp2BasicInfoFieldId.Status).value = LocationStatus.Inactive;
            }
        } else {
            const newStatus = findFieldById(formState, Mp2BasicInfoFieldId.Status).value;
            const maintenanceIntervalField = findFieldById(formState, Mp2BasicInfoFieldId.MaintenanceInterval);
            const maintenanceEndField = findFieldById(formState, Mp2BasicInfoFieldId.MaintenanceEnd, FieldType.UI);

            if (maintenanceEndField) {
                if (newStatus === 1) {
                    maintenanceIntervalField.visible = true;
                    maintenanceEndField.visible = true;

                    maintenanceIntervalField.value = 2;
                    maintenanceEndField.label = createSmartTranslationMessage(
                        'LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE',
                        {
                            date: 'unknown',
                        },
                    );
                } else {
                    maintenanceIntervalField.visible = false;
                    maintenanceEndField.visible = false;
                }
            }
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.evaluateScanRateLock, (state) => {
        const formState = { ...state.formState };
        const scanRateField = findFieldById(formState, BasicInfoCommonFieldIds.ScanRate, FieldType.Select);

        if (!scanRateField) {
            return state;
        }

        const sampleRateField = findFieldById(formState, BasicInfoCommonFieldIds.SampleRate, FieldType.Select);
        const fastRateField = findFieldById(formState, BasicInfoCommonFieldIds.FastRate, FieldType.Select);

        const sampleRateValue = sampleRateField?.value;
        const fastRateValue = fastRateField?.value;
        const shouldLock =
            sampleRateValue != null && fastRateValue != null && sampleRateValue === fastRateValue;

        if (shouldLock) {
            scanRateField.value = 0;
            scanRateField.disabled = true;
        } else {
            scanRateField.disabled = false;
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.setFormSettings, (state, { settings }) => {
        return { ...state, settings };
    }),

    on(LocationCardActions.setPermissions, (state, { permissions }) => {
        return { ...state, permissions };
    }),

    on(LocationCardActions.loadDiagnosticsSuccess, (state, { showDiagnostics, deviceTypes }) => {
        return { ...state, diagnostics: { showDiagnostics, deviceTypes } };
    }),

    on(LocationCardActions.loadLocationDetailsSuccess, (state, { details }) => {
        return { ...state, locationDetails: details };
    }),

    on(LocationCardActions.loadMonitorConfigurationSuccess, (state, { config }) => {
        return { ...state, monitorConfiguration: config };
    }),

    on(LocationCardActions.transitionToEditModeSuccess, (state) => {
        return {
            ...state,
            loading: false,
            pristine: true,
            valid: true,
        };
    }),

    on(LocationCardActions.markFormDirty, (state) => {
        return { ...state, pristine: false };
    }),
);
